import cloneDeep from "lodash.clonedeep";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { Col, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { FinancialBreakdownController } from "services/financialBreakdown/controller";
import { AccordionCard, Button, Table, useAccordionWizard } from "stories";
import { currencyStyle, parseCurrency } from "utils/customHooks/currencyFormat";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import { v4 as uuidv4 } from "uuid";

const tableHeaders = [
  {
    id: "name",
    title: "Cash Equity",
    align: "left",
  },
  {
    id: "share",
    title: "Share",
    align: "right",
  },
  {
    id: "amount",
    title: "Amount",
    align: "right",
  },
];

export const getColumnTotal = (rows, key) => {
  return rows.reduce((prev, current) => {
    if (!current) return Number(parseFloat(prev).toFixed(2));
    let currentV =
      typeof current[key] === "function"
        ? String(current[key](current, rows))
        : current[key];

    currentV = parseCurrency(String(currentV));
    if (isNaN(parseFloat(currentV))) return Number(parseFloat(prev).toFixed(2));
    return Number((parseFloat(prev) + parseFloat(currentV)).toFixed(2));
  }, 0);
};

const rowShareCalculator = (row, rows) => {
  const total = getColumnTotal(rows, "amount");
  if (!row.amount && row.amount !== 0) return 0 + "%";
  if (total === 0) return "0%";
  return currencyStyle(Number(((row.amount / total) * 100).toFixed(2))) + "%";
};

const rowCashEquityGenerator = (selectedRow, rows) => {
  let counter = 0;
  for (let row of rows) {
    if (row.props.deletable) counter++;
    if (row.uid === selectedRow.uid) {
      break;
    }
  }
  return "Investor " + counter;
};

const getEmptyRow = () => ({
  uid: uuidv4(),
  name: rowCashEquityGenerator,
  share: rowShareCalculator,
  amount: "",
  cash_equity: "investor",
  props: {
    deletable: true,
    name: {
      editable: true,
    },
    amount: {
      editable: true,
      className: "text-primary",
      type: "currency",
    },
  },
});

const rowProps = {
  name: {},
  share: {
    type: "number",
  },
  amount: {
    editable: true,
    className: "text-primary",
    type: "currency",
  },
};

const footerData = [
  {
    id: 1,
    name: "Total Cash Equity",
    share: "100%",
    amount: "$ 14,050,000",
    props: {
      amount: {
        type: "currency",
      },
    },
  },
];
const financialBreakdown = FinancialBreakdownController();
export const EquityBreakdown = forwardRef((props, ref) => {
  const { next } = useAccordionWizard();
  const { showToast } = useToast();
  const { id: opportunityId } = useParams();
  const [equityBreakdown, handleGetEquityBreakdown] = useFetchState();
  const [updateEquityBreakdown, sendUpdateEquityStructure] = useFetchState();
  const form = useForm({
    defaultValues: {
      rows: [],
    },
  });
  const {
    fields: rows,
    append: addRow,
    remove: onDelete,
    update: onUpdate,
  } = useFieldArray({
    control: form.control,
    name: "rows",
    keyName: "key",
  });

  useEffect(() => {
    handleGetEquityBreakdown(
      financialBreakdown.getEquityBreakdown,
      opportunityId
    );
  }, []); // eslint-disable-line

  useImperativeHandle(ref, () => ({
    refetch() {
      handleGetEquityBreakdown(
        financialBreakdown.getEquityBreakdown,
        opportunityId
      );
    },
  }));
  useEffect(() => {
    if (
      !updateEquityBreakdown.isLoading &&
      !updateEquityBreakdown.error &&
      updateEquityBreakdown.data === ""
    ) {
      showToast("Equity breakdown updated successfully", "success");
      next();
      if (props.onSave) props.onSave();
    }
  }, [updateEquityBreakdown]); // eslint-disable-line
  useEffect(() => {
    if (
      !equityBreakdown.isLoading &&
      !equityBreakdown.isError &&
      equityBreakdown.data
    ) {
      equityBreakdown.data.sort((a, b) =>
        String(a.cash_equity).localeCompare(b.cash_equity)
      );
      equityBreakdown.data.forEach((row) => {
        row.uid = uuidv4();
        row.props = cloneDeep(rowProps);
        if (String(row.cash_equity).toLowerCase() === "investor") {
          row.props.deletable = true;
          row.props.name.editable = true;
        } else {
          row.name = row.name ? row.name : row.cash_equity;
          row.props.name.className = "capitalize";
        }
      });
      form.reset({
        rows: equityBreakdown.data,
      });
    }
  }, [equityBreakdown]); // eslint-disable-line

  const handleAddRow = () => {
    addRow(getEmptyRow());
  };
  const save = ({ rows }) => {
    const data = rows.map((row) => ({
      id: typeof row.id === "number" ? row.id : undefined,
      amount: parseFloat(row.amount),
      cash_equity: row.cash_equity,
      name: row.cash_equity === "investor" ? row.name : undefined,
    }));
    const body = {
      rows: data,
      id: opportunityId,
    };
    sendUpdateEquityStructure(financialBreakdown.updateEquityBreakdown, body);
  };
  const onFieldChanged = (rowIndex, cellId, value) => {
    onUpdate(rowIndex, {
      ...rows[rowIndex],
      [cellId]: value,
    });
  };

  rows.map((row) => {
    row.share = rowShareCalculator;
    return row;
  });
  footerData[0].amount = "$ " + currencyStyle(getColumnTotal(rows, "amount"));
  footerData[0].share =
    currencyStyle(Math.round(getColumnTotal(rows, "share"), 1)) + "%";

  return (
    <div className={"mt-4"} ref={ref}>
      <AccordionCard
        headerText="Equity Breakdown"
        accordionKey="equity-breakdown"
      >
        <form onSubmit={form.handleSubmit(save)}>
          <div className={"fw-bold mb-3"}>Equity Breakdown</div>
          <div className="pr-2/5">
            <Table
              sameColumnWidth
              headers={tableHeaders}
              rows={rows}
              footer={footerData}
              onDelete={onDelete}
              onFieldChanged={onFieldChanged}
            />
          </div>

          <div
            className={"fw-bold text-primary mb-3 d-inline-block"}
            role={"button"}
            onClick={handleAddRow}
          >
            + Add Investor
          </div>
          <Row className={"mt-5"}>
            <Col />
            <Col xs={2}>
              <Button
                label={"Save and Next"}
                loading={updateEquityBreakdown.isLoading}
              />
            </Col>
          </Row>
        </form>
      </AccordionCard>
    </div>
  );
});
